import "./Search.css";
import { Link } from "react-router-dom";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import VivahApi from "../../api";
import { useEffect, useState, useMemo } from "react";
import { calculateAgeInYearsAndMonths } from "../../utils/common";
import { isPlanPurchased } from "../../utils/api-utils";

function buildLookup(items) {
  const map = {};
  (items || []).forEach(i => { map[String(i.sid)] = i.name || i.label || String(i.sid); });
  return map;
}

function SkeletonCard() {
  return (
    <div className="col-xs-12 col-sm-6 col-md-3">
      <div className="profile-card skeleton-card">
        <div className="skeleton skeleton-photo"></div>
        <div className="profile-card-body">
          <div className="skeleton skeleton-line wide"></div>
          <div className="skeleton skeleton-line medium"></div>
          <div className="skeleton skeleton-line narrow"></div>
          <div className="skeleton skeleton-line medium"></div>
          <div className="profile-card-actions">
            <div className="skeleton skeleton-btn"></div>
            <div className="skeleton skeleton-btn"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SearchResults() {
  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortFilter, setSortFilter] = useState("sid DESC");
  const [masterData, setMasterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favourites, setFavourites] = useState({});
  const [interests, setInterests] = useState({});
  const PAGE_COUNT = 8;

  useEffect(() => {
    VivahApi.profile.listProfile().then(res => setMasterData(res.data));
    // Pre-load existing favourites to reflect correct heart state
    VivahApi.Favourite.listFavourite().then(res => {
      const map = {};
      (res.data.data || []).forEach(f => { map[f.profileId] = true; });
      setFavourites(map);
    }).catch(() => {});
    // Pre-load sent interests so button shows correct state after reload
    VivahApi.Interest.getRequest('sent').then(res => {
      const map = {};
      (res.data.data || []).forEach(i => { map[i.toUserId] = true; });
      setInterests(map);
    }).catch(() => {});
  }, []);

  const lookups = useMemo(() => {
    if (!masterData) return {};
    return {
      religion: buildLookup(masterData.religions),
      caste: buildLookup(masterData.castes),
      motherTongue: buildLookup(masterData.motherTongues),
      state: buildLookup(masterData.states),
      city: buildLookup(masterData.cities),
      qualification: buildLookup(masterData.qualifications),
      profession: buildLookup(masterData.professions),
      height: buildLookup(masterData.heights),
      maritalStatus: buildLookup(masterData.maritalStatuses),
    };
  }, [masterData]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const res = await VivahApi.Search.getSearch(sortFilter, PAGE_COUNT, (page - 1) * PAGE_COUNT);
        setProfiles(res.data.profiles || []);
        setTotalPage(Math.ceil(res.data.count / PAGE_COUNT));
        setTotalCount(res.data.count);
      } catch (e) {
        setProfiles([]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [page, sortFilter]);

  const resolve = (lookupName, id) => {
    if (!id || !lookups[lookupName]) return '';
    return lookups[lookupName][String(id)] || '';
  };

  const handleFavourite = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const profileId = p.sid || p.userId;
    if (favourites[profileId]) {
      await VivahApi.Favourite.removeFavourite(profileId).catch(() => {});
      setFavourites(prev => { const n = { ...prev }; delete n[profileId]; return n; });
    } else {
      await VivahApi.Favourite.addFavourite(profileId).catch(() => {});
      setFavourites(prev => ({ ...prev, [profileId]: true }));
    }
  };

  const handleSendInterest = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const profileId = p.sid || p.userId;
    if (interests[profileId]) return;
    try {
      await VivahApi.Interest.addInterest(profileId);
      setInterests(prev => ({ ...prev, [profileId]: true }));
    } catch (err) {
      // Backend returns 400 when interest already exists
      if (err?.response?.status === 400 || err?.response?.status === 409) {
        setInterests(prev => ({ ...prev, [profileId]: true }));
      }
    }
  };

  const fromResult = (page - 1) * PAGE_COUNT + 1;
  const toResult = Math.min(page * PAGE_COUNT, totalCount);

  return (
    <>
      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />
        {/* <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">Discover</h1>
              </div>
              <div className="col-md-4">
                <div className="breadcrumbs">
                  <a href="/">Home</a><span>Discover</span>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <section id="gla_content" className="gla_content page-top-offset">
          <section className="gla_section">
            <div className="container">

              {/* Results header */}
              <div className="matches-header">
                <div className="matches-count">
                  {loading ? 'Finding your matches...' : totalCount === 0 ? 'No profiles found' : `Showing ${fromResult}–${toResult} of ${totalCount} profiles`}
                </div>
                <div className="matches-sort">
                  <label htmlFor="sort-select">Sort by</label>
                  <select
                    id="sort-select"
                    value={sortFilter}
                    onChange={(e) => { setPage(1); setSortFilter(e.target.value); }}
                  >
                    <option value="sid DESC">Newest first</option>
                    <option value="age DESC">Age: high to low</option>
                    <option value="age ASC">Age: low to high</option>
                  </select>
                </div>
              </div>

              {/* Cards grid */}
              <div className="row matches-grid">
                {loading && [...Array(PAGE_COUNT)].map((_, i) => <SkeletonCard key={i} />)}

                {!loading && totalCount === 0 && (
                  <div className="matches-empty">
                    <i className="ti ti-heart-broken"></i>
                    <h3>No matches found</h3>
                    <p>Try adjusting your search filters to see more profiles.</p>
                    <a href="/search" className="matches-empty-cta">Edit Filters</a>
                  </div>
                )}

                {!loading && profiles.map((p) => {
                  const profileId = p.sid || p.userId;
                  const name = p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown';
                  const age = p.dateOfBirth ? calculateAgeInYearsAndMonths(new Date(p.dateOfBirth)) : null;
                  const religion = resolve('religion', p.religionId);
                  const location = [resolve('city', p.cityId), resolve('state', p.stateId)].filter(Boolean).join(', ');
                  const qualification = resolve('qualification', p.qualificationId);
                  const profession = resolve('profession', p.professionId);
                  const isFav = !!favourites[profileId];
                  const sentInterest = !!interests[profileId];

                  return (
                    <div className="col-xs-12 col-sm-6 col-md-3" key={profileId}>
                      <div className="profile-card">
                        {/* Photo */}
                        <Link to={`/profile/${p.sid || p.userId}`} className="profile-card-photo-wrap">
                          <img
                            src={p.profilePhoto || "/assets/images/avatar-placeholder.svg"}
                            alt={name}
                            className="profile-card-photo"
                          />
                          <span className="profile-card-badge">Premium</span>
                        </Link>

                        {/* Body */}
                        <div className="profile-card-body">
                          <Link to={`/profile/${p.sid || p.userId}`} className="profile-card-name">{name}</Link>
                          {age && <span className="profile-card-age">{age}</span>}

                          <div className="profile-card-chips">
                            {religion && (
                              <span className="pcc">
                                <i className="ti ti-shine"></i> {religion}
                              </span>
                            )}
                            {location && (
                              <span className="pcc">
                                <i className="ti ti-location-pin"></i> {location}
                              </span>
                            )}
                            {qualification && (
                              <span className="pcc">
                                <i className="ti ti-shield"></i> {qualification}
                              </span>
                            )}
                            {profession && (
                              <span className="pcc">
                                <i className="ti ti-briefcase"></i> {profession}
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="profile-card-actions">
                            <button
                              className={`pca-fav${isFav ? ' active' : ''}`}
                              onClick={(e) => handleFavourite(e, p)}
                              title={isFav ? 'Remove from favourites' : 'Add to favourites'}
                            >
                              <i className={`ti ti-heart${isFav ? '' : '-broken'}`}></i>
                              {isFav ? 'Saved' : 'Save'}
                            </button>
                            <button
                              className={`pca-interest${sentInterest ? ' sent' : ''}`}
                              onClick={(e) => handleSendInterest(e, p)}
                              disabled={sentInterest}
                              title={sentInterest ? 'Interest sent' : 'Send interest'}
                            >
                              <i className="ti ti-email"></i>
                              {sentInterest ? 'Sent' : 'Send Interest'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {!loading && totalPage > 1 && (
                <nav className="matches-pagination">
                  <button
                    className="mpag-btn"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                  >
                    <i className="ti ti-angle-left"></i> Prev
                  </button>

                  <div className="mpag-pages">
                    {[...Array(totalPage)].map((_, i) => (
                      <button
                        key={i}
                        className={`mpag-page${page === i + 1 ? ' active' : ''}`}
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    className="mpag-btn"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page === totalPage}
                  >
                    Next <i className="ti ti-angle-right"></i>
                  </button>
                </nav>
              )}

            </div>
          </section>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default SearchResults;
